import { useMemo } from 'react';
import { Customer } from '../../../types';
import { useCustomerService } from '../services/customerService';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';
import { OPERATIONS_ACCOUNT_REGION } from '../../../shared/constants/aws';

export interface CustomerFormData {
  name: string;
  operations_account_id: string;
  operations_account_role: string;
  operations_account_write_role: string;
  operations_account_automation_role: string;
  operations_account_dataset_role: string;
  operations_account_external_id: string;
  operations_account_region: string;
  operations_event_bus_name: string;
  knowledge_base_id: string;
  knowledge_base_bucket: string;
  portal_url: string;
}

const EMPTY_FORM: CustomerFormData = {
  name: '',
  operations_account_id: '',
  operations_account_role: '',
  operations_account_write_role: '',
  operations_account_automation_role: '',
  operations_account_dataset_role: '',
  operations_account_external_id: '',
  operations_account_region: OPERATIONS_ACCOUNT_REGION,
  operations_event_bus_name: '',
  knowledge_base_id: '',
  knowledge_base_bucket: '',
  portal_url: '',
};

export const getAccountIdError = (accountId: string): string | null => {
  if (!accountId) return null;
  if (!/^\d+$/.test(accountId)) return "Must contain only numbers";
  if (accountId.length < 12) return "Must be exactly 12 digits";
  if (accountId.length > 12) return "Must be exactly 12 digits";
  return null;
};

export const getRoleNameError = (roleName: string): string | null => {
  if (!roleName) return null;
  if (!/^[\w+=,.@-]+$/.test(roleName)) {
    return "Invalid role name format (allowed: alphanumeric, +=,.@-)";
  }
  if (roleName.length > 64) return "Must be 64 characters or less";
  return null;
};

interface UseCustomerFormOptions {
  isOpen: boolean;
  onClose: () => void;
  onCustomerUpdated?: () => void;
  initialSelectedCustomerId?: string;
}

export function useCustomerForm({
  isOpen,
  onClose,
  onCustomerUpdated,
  initialSelectedCustomerId,
}: UseCustomerFormOptions) {
  const customerService = useCustomerService();

  const crudService = useMemo((): EntityCrudService<Customer, Omit<Customer, 'id' | 'created_at' | 'updated_at'>, Partial<Customer>> => ({
    getAll: () => customerService.getCustomers(),
    create: (dto) => customerService.createCustomer(dto),
    update: (id, dto) => customerService.updateCustomer(id, dto),
    delete: (id) => customerService.deleteCustomer(id),
    testAccess: (id, roleType: 'readonly' | 'write') => customerService.testOperationsRoleAccess(id, roleType)
  }), [customerService]);

  const entityManagement = useEntityManagement<Customer, CustomerFormData>({
    service: crudService,
    queryKey: ['customer', 'customers'],
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (customer) => customer.id,
    entityToForm: (customer) => ({
      name: customer.name,
      operations_account_id: customer.operations_account_id,
      operations_account_role: customer.operations_account_role || '',
      operations_account_write_role: customer.operations_account_write_role || '',
      operations_account_automation_role: customer.operations_account_automation_role || '',
      operations_account_dataset_role: customer.operations_account_dataset_role || '',
      operations_account_external_id: customer.operations_account_external_id || '',
      operations_account_region: customer.operations_account_region,
      operations_event_bus_name: customer.operations_event_bus_name || '',
      knowledge_base_id: customer.knowledge_base_id || '',
      knowledge_base_bucket: customer.knowledge_base_bucket || '',
      portal_url: customer.portal_url || '',
    }),
    formToCreateDto: (form) => ({
      name: form.name,
      operations_account_id: form.operations_account_id,
      operations_account_role: form.operations_account_role || undefined,
      operations_account_write_role: form.operations_account_write_role || undefined,
      operations_account_automation_role: form.operations_account_automation_role || undefined,
      operations_account_dataset_role: form.operations_account_dataset_role || undefined,
      operations_account_external_id: form.operations_account_external_id || undefined,
      operations_account_region: form.operations_account_region,
      operations_event_bus_name: form.operations_event_bus_name || undefined,
      knowledge_base_id: form.knowledge_base_id || undefined,
      knowledge_base_bucket: form.knowledge_base_bucket || undefined,
      portal_url: form.portal_url || undefined,
    }),
    formToUpdateDto: (form) => ({
      name: form.name,
      operations_account_id: form.operations_account_id,
      operations_account_role: form.operations_account_role || undefined,
      operations_account_write_role: form.operations_account_write_role || undefined,
      operations_account_automation_role: form.operations_account_automation_role || undefined,
      operations_account_dataset_role: form.operations_account_dataset_role || undefined,
      operations_account_external_id: form.operations_account_external_id || undefined,
      operations_account_region: form.operations_account_region,
      operations_event_bus_name: form.operations_event_bus_name || undefined,
      knowledge_base_id: form.knowledge_base_id || undefined,
      knowledge_base_bucket: form.knowledge_base_bucket || undefined,
      portal_url: form.portal_url || undefined,
    }),
    validateForm: (form) => ({
      name: !form.name.trim() ? 'Customer name is required' : null,
      operations_account_id: !form.operations_account_id || form.operations_account_id.length !== 12 || !/^\d{12}$/.test(form.operations_account_id)
        ? 'Must be a 12-digit AWS account ID' : getAccountIdError(form.operations_account_id),
      operations_account_role: form.operations_account_role ? getRoleNameError(form.operations_account_role) : null,
      operations_account_write_role: form.operations_account_write_role ? getRoleNameError(form.operations_account_write_role) : null,
      operations_account_automation_role: form.operations_account_automation_role ? getRoleNameError(form.operations_account_automation_role) : null,
      operations_account_dataset_role: form.operations_account_dataset_role ? getRoleNameError(form.operations_account_dataset_role) : null,
    }),
    hasFormChanges: (form, customer, isNew) => {
      if (isNew) return true;
      if (!customer) return false;
      return form.name !== customer.name ||
        form.operations_account_id !== customer.operations_account_id ||
        form.operations_account_role !== (customer.operations_account_role || '') ||
        form.operations_account_write_role !== (customer.operations_account_write_role || '') ||
        form.operations_account_automation_role !== (customer.operations_account_automation_role || '') ||
        form.operations_account_dataset_role !== (customer.operations_account_dataset_role || '') ||
        form.operations_account_external_id !== (customer.operations_account_external_id || '') ||
        form.operations_event_bus_name !== (customer.operations_event_bus_name || '') ||
        form.knowledge_base_id !== (customer.knowledge_base_id || '') ||
        form.knowledge_base_bucket !== (customer.knowledge_base_bucket || '') ||
        form.portal_url !== (customer.portal_url || '');
    },
    onEntitiesUpdated: onCustomerUpdated,
    initialSelectedId: initialSelectedCustomerId
  });

  const {
    entities: customers,
    loading,
    error,
    selectedEntity: selectedCustomer,
    formData,
    saving,
    isNew: isNewCustomer,
    showDeleteConfirm,
    testing,
    testResult,
    handleSelect: handleSelectCustomer,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    handleTestRole,
    loadEntities,
    setError,
    setSuccessMessage,
    successMessage,
    isFormValid,
    hasChanges,
    fieldErrors,
    hasAttemptedSave,
  } = entityManagement;

  return {
    customers,
    loading,
    error,
    selectedCustomer,
    formData,
    saving,
    isNewCustomer,
    showDeleteConfirm,
    testing,
    testResult,
    successMessage,
    hasAttemptedSave,
    fieldErrors,
    isFormValid,
    hasChanges,
    handleSelect: handleSelectCustomer,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    handleTestRole,
    handleClose: onClose,
    setError,
    setSuccessMessage,
    loadEntities,
  };
}
